import React, { JSX, useEffect, useState } from "react";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { TextAlignment, WafflesThemes } from "@app/shared/waffles/exports";
import { QESButton } from "@app/shared/qes-standards/Button";
import { valueOrDashes } from "@app/shared/utility/valueOrDashes";
import { BenchmarkingProviderFlyoutData, BenchmarkingProviderContractDetails } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingProviderFlyoutData";

interface BenchmarkingProviderFlyoutAddressTableProps {
  benchmarkingProviderFlyoutData: BenchmarkingProviderFlyoutData;
  loading: boolean;
  npi: string;
  formatNetworkList: (networkList: string[], isFlyout?: boolean) => JSX.Element;
}

interface BenchmarkingProviderFlyoutAddressTableData {
  associatedAddresses: JSX.Element;
  network: JSX.Element;
  [wafflesKeyColumnName]: string;
}

const columnConfig: ColumnConfig = {
  associatedAddresses: {
    alignment: TextAlignment.Left,
    displayText: "Associated Addresses",
    noResize: true,
    noSort: true,
    testId: "associated-addresses",
  },
  network: {
    alignment: TextAlignment.Left,
    displayText: "Network",
    noResize: true,
    noSort: true,
    testId: "network",
  },
};

const CompetitiveInsightsAddressesByNetwork = (props: BenchmarkingProviderContractDetails): JSX.Element => {
  const [loadingAll, setLoadingAll] = useState<boolean>(false);

  return (
    <div className="row">
      <div className="col-12">
        <React.Fragment>
          {(loadingAll ? props.addresses : props.addresses.slice(0, 10)).map((address, index) => (
            <div className="mb-3" key={index}>
              <div>
                {address.address}
              </div>
              {address.address2 && (
                <div>
                  {address.address2}
                </div>
              )}
              <div>
                {address.city}, {address.state} {address.zip}
              </div>
              <div>
                <span className="demibold">Location Confidence Score: {valueOrDashes(address.locationConfidenceScoreDisplay)}</span>
              </div>
            </div>
          ))}
        </React.Fragment>
        {(props.addresses.length > 10) && (
          <QESButton
            qesTestId={`competitive-insights__addresses-by-network--table-show-more-less-${props.contractId}`}
            qesType="secondary"
            onClick={() => setLoadingAll(!loadingAll)}
            className="px-0"
          >
            Show {loadingAll ? "Less" : "All"}
          </QESButton>
        )}
      </div>
    </div>
  )
}

const BenchmarkingProviderFlyoutAddressTable = (props: BenchmarkingProviderFlyoutAddressTableProps): JSX.Element => {
  const [tableData, setTableData] = useState<BenchmarkingProviderFlyoutAddressTableData[]>([]);

  useEffect(() => {
    /* oxlint-disable sort-keys */
    const newTableData: BenchmarkingProviderFlyoutAddressTableData[] = props.benchmarkingProviderFlyoutData?.contractDetails?.map((x, index) => {
      return {
        [wafflesKeyColumnName]: x.contractId || index.toString(),
        network: <div className="me-5">{props.formatNetworkList([x.contractId], true)}</div>,
        associatedAddresses: (
          <CompetitiveInsightsAddressesByNetwork
            addresses={x.addresses}
            contractId={x.contractId}
          />
        ),
      }
    }) || [];
    /* oxlint-enable sort-keys */

    setTableData(newTableData);
  }, [props.benchmarkingProviderFlyoutData]);

  return (
    <div className="row">
      <div className="col-12">
        <Waffles<BenchmarkingProviderFlyoutAddressTableData>
          columns={columnConfig}
          data={tableData}
          fullWidth={true}
          loading={props.loading}
          testId={`benchmarking-dashboard--provider-flyout__address-table-${props.npi}`}
          theme={WafflesThemes.Simple}
        />
      </div>
    </div>
  )
}

export default BenchmarkingProviderFlyoutAddressTable;